/**
 * Pick one person, or several, from the people GitLab holds for a project
 * (issue #67): the assignee of new work, the reviewers of a merge request.
 *
 * BEST encodes no review or assignment policy, and this picker is where that
 * is easiest to break, so what it does and does not do is stated here:
 *
 *   - IT SHOWS, IT DOES NOT REQUIRE. People GitLab says can merge are MARKED
 *     ("can merge", naming the branch) and sorted up the list. Nobody is
 *     filtered out, nobody is labelled "cannot merge", and nothing here
 *     disables a control or blocks a submit because of who was or was not
 *     chosen. `false` and `null` carry no mark at all: null means GitLab's rule
 *     could not be resolved, and an absent mark must not read as a verdict.
 *   - IT DOES NOT ASK. The list arrives from the caller (`useMembers`, in
 *     members.ts) because the dialogs need it too. The picker filters it in
 *     the browser, by name and by username, as you type.
 *   - IT CAN FAIL AND THE FORM CARRIES ON. Assigning is optional everywhere
 *     it appears, so when the list cannot be read the field says so in a short
 *     line under itself and stays empty; the surrounding form is untouched.
 *
 * Two traps carried over from ElementPicker: a value the list does not hold (the
 * caller when GitLab does not list them as a member) is still offered, or MUI
 * renders a blank box and warns; and `slotProps` entries are merged, never
 * replaced, because `htmlInput` carries the ref for the real <input>.
 */

import type { ReactNode } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { PersonMergeHint } from '../../schema'
import { canMergeText, matchesPerson, orderPeople } from './members'
import type { MembersState } from './members'

type Common = {
  label: string
  members: MembersState
  helperText?: ReactNode
  disabled?: boolean
  fullWidth?: boolean
}

export type PeoplePickerProps = Common &
  (
    | {
        multiple?: false
        value: PersonMergeHint | null
        onChange: (value: PersonMergeHint | null) => void
      }
    | { multiple: true; value: PersonMergeHint[]; onChange: (value: PersonMergeHint[]) => void }
  )

/**
 * The small mark beside a person GitLab says can merge. Nothing for anyone else.
 * Exported so the merge request's card marks its reviewers the same way.
 */
export function CanMergeMark({ branch }: { branch: string | undefined }) {
  const text = canMergeText(branch)
  return (
    <Tooltip title={text} describeChild>
      <Chip
        label="can merge"
        size="small"
        color="success"
        variant="outlined"
        aria-label={text}
        sx={{ height: 20, fontSize: '0.7rem' }}
      />
    </Tooltip>
  )
}

export default function PeoplePicker(props: PeoplePickerProps) {
  const { label, members, helperText, disabled, fullWidth = true } = props
  const data = members.status === 'ready' ? members.data : null
  const branch = data?.merge_rule.branch

  const values = props.multiple ? props.value : props.value ? [props.value] : []

  // The current value is always an option: Autocomplete cannot render a value
  // it cannot find. Listed members win, so the mark reflects the project's own
  // answer rather than whatever the value was built with.
  const listed = data?.items ?? []
  const listedIds = new Set(listed.map((person) => person.id))
  const options = orderPeople(
    [...listed, ...values.filter((person) => !listedIds.has(person.id))],
    data?.me.id ?? null,
  )

  const loading = members.status === 'loading'
  const failure =
    members.status === 'failed'
      ? `The list of people could not be loaded (${members.message}). You can carry on without choosing anyone.`
      : ''

  const selected = props.multiple ? values : (values[0] ?? null)

  return (
    <Autocomplete<PersonMergeHint, boolean>
      multiple={props.multiple}
      disabled={disabled}
      options={options}
      value={selected as never}
      onChange={(_event, next) => {
        if (props.multiple) props.onChange(next as PersonMergeHint[])
        else props.onChange(next as PersonMergeHint | null)
      }}
      getOptionLabel={(person) => person.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      // Name OR username, in the browser. When the box shows the chosen
      // person's own name (single mode) the full list is offered, or opening
      // the field after choosing would show only the one person.
      filterOptions={(all, state) => {
        const typed = state.inputValue
        if (!typed.trim() || all.some((person) => person.name === typed)) return all
        return all.filter((person) => matchesPerson(person, typed))
      }}
      loading={loading}
      loadingText="Reading the people…"
      noOptionsText={failure ? 'No people to choose from' : 'Nobody matches that'}
      size="small"
      fullWidth={fullWidth}
      clearOnBlur={false}
      slotProps={{ popper: { placement: 'bottom-start', sx: { minWidth: 320 } } }}
      renderOption={({ key, ...optionProps }, person) => (
        <Box component="li" key={key} {...optionProps} sx={{ gap: 1 }}>
          <Typography variant="body2">{person.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            @{person.username}
          </Typography>
          {person.can_merge === true ? <CanMergeMark branch={branch} /> : null}
        </Box>
      )}
      renderValue={
        props.multiple
          ? (chosen, getItemProps) =>
              // Generic over a boolean `multiple`, so the type cannot know it is a list.
              (chosen as PersonMergeHint[]).map((person, index) => (
                <Chip
                  key={person.id}
                  {...getItemProps({ index })}
                  size="small"
                  // A chip is where the mark has to survive: the option row is
                  // gone once the list closes. Words, not just a colour.
                  label={person.can_merge === true ? `${person.name} · can merge` : person.name}
                  title={person.can_merge === true ? canMergeText(branch) : `@${person.username}`}
                  variant="outlined"
                />
              ))
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={failure || helperText}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  )
}
